import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ClinicCombobox } from '@/components/shared/ClinicCombobox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAdminClearMfa, useCreateInvitation, useCreateRole, useDeleteRole, useDisableUser, useEnableUser, useInvitations, usePermissions, useResendInvitation, useResetPassword, useRevokeInvitation, useRoles, useUpdateRole, useUpdateUser, useUserDetail, useUsers, } from '@/hooks/use-admin';
import { useEffectiveClinics } from '@/hooks/use-current-user';
import { Info } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InvitationList } from './InvitationList';
import { InviteUserModal } from './InviteUserModal';
import { RoleList } from './RoleList';
import { UserDetailPanel } from './UserDetailPanel';
import { UserList } from './UserList';
// Helper to convert API GetUsersResponse to SettingsUser for list view
function toSettingsUser(user) {
    return {
        id: user.id,
        email: user.email,
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phone: user.phone || undefined,
        avatarUrl: user.avatarUrl || undefined,
        clinicRoles: user.clinicRoles.map((cr) => ({
            clinicId: cr.clinicId,
            clinicCode: cr.clinicCode,
            clinicName: cr.clinicName,
            status: cr.status,
            roleId: cr.roleId,
            roleName: cr.roleName,
            roleColor: cr.roleColor,
            rolePermissions: [],
            permissionOverrides: [],
        })),
        permissions: [],
        lastLoginAt: user.lastLoggedInAt || undefined,
        createdAt: user.createdAt,
        updatedAt: user.createdAt,
    };
}
// Helper to convert API UserDetailResponse to SettingsUser with per-clinic permission data
function toSettingsUserDetail(user) {
    return {
        id: user.id,
        email: user.email,
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phone: user.phone || undefined,
        avatarUrl: user.avatarUrl || undefined,
        clinicRoles: user.clinicRoles.map((cr) => ({
            clinicId: cr.clinicId,
            clinicCode: cr.clinicCode,
            clinicName: cr.clinicName,
            status: cr.status,
            roleId: cr.roleId,
            roleName: cr.roleName,
            roleColor: cr.roleColor,
            rolePermissions: cr.rolePermissions,
            permissionOverrides: cr.permissionOverrides,
        })),
        permissions: [],
        lastLoginAt: user.lastLoggedInAt || undefined,
        createdAt: user.createdAt,
        updatedAt: user.createdAt,
    };
}
// Helper to convert API RoleResponse to settings Role for UI components
function toSettingsRole(role) {
    return {
        id: role.id,
        name: role.name,
        description: role.description || '',
        color: role.color,
        isSystem: role.isSystem,
        permissions: role.permissions,
        userCount: role.userCount,
        createdAt: role.createdAt,
        updatedAt: role.updatedAt,
    };
}
// Helper to convert API InvitationResponse to UI-compatible shape
function toInvitationItem(invitation) {
    return {
        id: invitation.id,
        email: invitation.email,
        firstName: invitation.firstName,
        lastName: invitation.lastName,
        roleId: invitation.roleId,
        status: invitation.status,
        sentByName: invitation.sentByName,
        sentAt: invitation.sentAt,
        expiresAt: invitation.expiresAt,
        acceptedAt: invitation.acceptedAt,
    };
}
export function UsersPermissionsSection() {
    const { t } = useTranslation('settings');
    // _requestedTab tracks what the user last clicked; activeTab derives from it.
    const [_requestedTab, setActiveTab] = useState('users');
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [showInviteModal, setShowInviteModal] = useState(false);
    // Super admins can manage users / invite / pick clinics in the roles tab across
    // every clinic in the system, not just their user_clinic memberships. The shared
    // useEffectiveClinics hook handles this asymmetry: regular users get clinics
    // where they hold manage_users; super admins get every active clinic via
    // GET /clinic. Backend authorization (CreateInvitation, etc.) already mirrors
    // this rule.
    const { clinics: manageUsersClinics } = useEffectiveClinics('manage_users');
    // Clinics eligible for manual invitations — SSO clinics are excluded because
    // their users are provisioned automatically on first sign-in. When this list
    // is empty (all managed clinics have SSO), the entire invite affordance is
    // hidden and replaced by an informational note.
    const invitePickerClinics = manageUsersClinics.filter((c) => !c.ssoConfigured);
    const allClinicsAreSso = manageUsersClinics.length > 0 && invitePickerClinics.length === 0;
    // Derived active tab: if the Invitations tab is no longer available (all clinics
    // became SSO while it was active) fall back to Users so Tabs never points at a
    // missing trigger. Derived at render time — no effect needed.
    const activeTab = allClinicsAreSso && _requestedTab === 'invitations' ? 'users' : _requestedTab;
    // User-selected clinic for the roles tab; empty until explicitly chosen
    const [selectedClinicId, setSelectedClinicId] = useState('');
    // Effective clinic: user selection if set, otherwise first available clinic
    const activeClinicId = selectedClinicId || manageUsersClinics[0]?.clinicId || '';
    // API hooks
    const { data: usersData, isLoading: isLoadingUsers } = useUsers();
    const { data: rolesData, isLoading: isLoadingRoles } = useRoles(activeClinicId || undefined);
    const { data: invitationsData, isLoading: isLoadingInvitations } = useInvitations();
    const { data: permissionsData, isLoading: isLoadingPermissions } = usePermissions();
    const { data: userDetailData } = useUserDetail(selectedUserId);
    // Extract permissions data
    const permissionCategories = permissionsData?.categories ?? [];
    const permissionDependencies = permissionsData?.dependencies ?? [];
    const totalPermissionCount = permissionCategories.reduce((sum, cat) => sum + cat.permissions.length, 0);
    // Mutations
    const createRole = useCreateRole();
    const updateRole = useUpdateRole();
    const deleteRole = useDeleteRole();
    const updateUser = useUpdateUser();
    const disableUser = useDisableUser();
    const enableUser = useEnableUser();
    const resetPassword = useResetPassword();
    const clearMfa = useAdminClearMfa();
    const createInvitation = useCreateInvitation();
    const resendInvitation = useResendInvitation();
    const revokeInvitation = useRevokeInvitation();
    // Convert API responses to UI types
    const users = usersData?.items.map(toSettingsUser) || [];
    const roles = rolesData?.map(toSettingsRole) || [];
    const invitations = invitationsData?.map(toInvitationItem) || [];
    // Convert selected user detail to SettingsUser (includes permission overrides)
    const selectedUser = userDetailData ? toSettingsUserDetail(userDetailData) : null;
    const handleUserSelect = (user) => {
        setSelectedUserId(user.id);
    };
    const handleCloseUserDetail = () => {
        setSelectedUserId(null);
    };
    const handleInviteUser = () => {
        setShowInviteModal(true);
    };
    // Keep the detail panel open after toggling access for a clinic — the ['user', id]
    // invalidation refetches and the selected clinic's Disable/Enable control flips in place,
    // so an admin managing a multi-clinic user isn't bounced back to the list each time.
    const handleDisableUser = (userId, clinicId) => {
        disableUser.mutate({ id: userId, clinicId });
    };
    const handleEnableUser = (userId, clinicId) => {
        enableUser.mutate({ id: userId, clinicId });
    };
    const handleSaveClinicRole = (userId, clinicId, data) => {
        updateUser.mutate({ id: userId, request: { clinicId, ...data } });
    };
    const handleResetPassword = (userId, clinicId) => {
        resetPassword.mutate({ id: userId, clinicId });
    };
    const handleClearMfa = (userId, clinicId) => {
        clearMfa.mutate({ id: userId, clinicId });
    };
    const handleCreateRole = (role) => {
        if (!activeClinicId)
            return;
        createRole.mutate({
            name: role.name || '',
            description: role.description,
            color: role.color || 'gray',
            clinicId: activeClinicId,
            permissions: role.permissions || [],
        });
    };
    const handleUpdateRole = (role) => {
        if (!role.id)
            return;
        updateRole.mutate({
            id: role.id,
            request: {
                name: role.name,
                description: role.description,
                color: role.color,
                permissions: role.permissions,
            },
        });
    };
    const handleDeleteRole = (roleId) => {
        deleteRole.mutate(roleId);
    };
    const handleResendInvitation = (invitation) => {
        resendInvitation.mutate(invitation.id);
    };
    const handleRevokeInvitation = (invitation) => {
        revokeInvitation.mutate(invitation.id);
    };
    const handleInviteSent = (data) => {
        createInvitation.mutate(data, {
            onSuccess: () => {
                setShowInviteModal(false);
            },
        });
    };
    // When a user is selected in the Users tab, show full-width detail view
    if (selectedUser && activeTab === 'users') {
        return (_jsx("div", { className: "flex h-full flex-col", children: _jsx(UserDetailPanel, { user: selectedUser, roles: roles, onBack: handleCloseUserDetail, onSaveClinicRole: handleSaveClinicRole, onResetPassword: handleResetPassword, onClearMfa: handleClearMfa, onDisableUser: handleDisableUser, onEnableUser: handleEnableUser, isSaving: updateUser.isPending, permissionCategories: permissionCategories, permissionDependencies: permissionDependencies }) }));
    }
    return (_jsxs("div", { className: "flex h-full flex-col", children: [_jsxs(Tabs, { value: activeTab, onValueChange: setActiveTab, className: "flex h-full flex-col", children: [_jsxs(TabsList, { className: "mb-4 w-fit", children: [_jsx(TabsTrigger, { value: "users", children: t('users.tab.users') }), _jsx(TabsTrigger, { value: "roles", children: t('users.tab.roles') }), !allClinicsAreSso && (_jsx(TabsTrigger, { value: "invitations", children: t('users.tab.invitations') }))] }), _jsx("div", { className: "relative flex min-h-0 flex-1", children: _jsxs("div", { className: "min-w-0 flex-1", children: [_jsxs(TabsContent, { value: "users", className: "m-0 h-full", children: [allClinicsAreSso && (_jsxs("div", { className: "mb-4 flex items-start gap-2 rounded-lg border border-blue-500/20 bg-blue-500/10 p-3 text-sm", children: [_jsx(Info, { className: "mt-0.5 h-4 w-4 shrink-0 text-blue-400" }), _jsx("p", { className: "text-blue-200", children: t('users.sso.inviteNote') })] })), _jsx(UserList, { users: users, roles: roles, isLoading: isLoadingUsers || isLoadingRoles, showClinicPrefix: manageUsersClinics.length > 1, onUserSelect: handleUserSelect, onInviteUser: handleInviteUser, hideInviteButton: allClinicsAreSso })] }), _jsxs(TabsContent, { value: "roles", className: "m-0 flex h-full flex-col", children: [manageUsersClinics.length > 1 && (_jsxs("div", { className: "mb-3 flex items-center gap-2", children: [_jsx("span", { className: "text-muted-foreground text-sm", children: t('users.clinic') }), _jsx(ClinicCombobox, { clinics: manageUsersClinics, value: activeClinicId, onChange: setSelectedClinicId, "aria-label": t('users.clinic'), wrapperClassName: "w-48" })] })), _jsx(RoleList, { roles: roles, isLoading: isLoadingRoles || isLoadingPermissions, onCreateRole: handleCreateRole, onUpdateRole: handleUpdateRole, onDeleteRole: handleDeleteRole, permissionCategories: permissionCategories, permissionDependencies: permissionDependencies, totalPermissionCount: totalPermissionCount })] }), _jsx(TabsContent, { value: "invitations", className: "m-0 h-full", children: _jsx(InvitationList, { invitations: invitations, roles: roles, clinics: invitePickerClinics, isLoading: isLoadingInvitations || isLoadingRoles, onResend: handleResendInvitation, onRevoke: handleRevokeInvitation, onInvite: handleInviteSent }) })] }) })] }), _jsx(InviteUserModal, { open: showInviteModal, onOpenChange: setShowInviteModal, onInvite: handleInviteSent, roles: roles, clinics: invitePickerClinics, isLoading: createInvitation.isPending })] }));
}
